import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';

import { api } from '../api/cliente';
import type { Grupo, Membro, Roadmap as RoadmapDados, Tarefa } from '../api/tipos';
import { useSessao } from '../auth/SessaoContext';
import { TituloDaPagina } from '../shell/TituloDaPagina';
import { Kanban } from './Kanban';
import { ModalFrente } from './ModalFrente';
import { CHAVE_ROADMAP, useMutacoesRoadmap } from './roadmapApi';
import { Avatar } from '../comum/Avatar';
import './roadmap.css';

const SLUG = 'mistik-island';

type Modo = 'timeline' | 'kanban';

/** Onde o arrasto vai soltar: antes/depois de uma frente, ou no fim de um bloco. */
type AlvoDrag =
  | { tipo: 'frente'; tarefaId: number; antes: boolean }
  | { tipo: 'bloco'; grupoId: number };

/**
 * O planejamento completo: timeline e kanban sobre o mesmo agregado, modal de edição,
 * checklists e reordenação por arrasto — o comportamento do módulo original, agora
 * com o dado compartilhado no servidor.
 */
export function Roadmap() {
  const filaDeQueries = useQueryClient();
  const { usuario } = useSessao();
  const { criarTarefa, editarTarefa, moverTarefa } = useMutacoesRoadmap();

  const [modo, setModo] = useState<Modo>('timeline');
  const [ocultos, setOcultos] = useState<Set<number>>(new Set());
  /**
   * Qual frente está aberta no modal — a URL é quem manda.
   *
   * <p>`?frente=12` abre a frente 12. É assim que uma notificação leva direto ao que ela
   * fala; sem isso, clicar num aviso jogaria a pessoa no topo de uma timeline com 57
   * frentes para procurar a certa.
   *
   * <p>Mora na URL, e não num `useState`, por dois motivos: quem já está no Roadmap e
   * clica noutro aviso vê o modal trocar de frente (um estado local não reagiria, porque
   * o componente não remonta), e o endereço fica copiável — dá para mandar a frente para
   * alguém no chat.
   */
  const [busca, setBusca] = useSearchParams();
  const frenteAberta = busca.get('frente') ? Number(busca.get('frente')) : null;

  function setFrenteAberta(id: number | null) {
    setBusca(
      (atual) => {
        const nova = new URLSearchParams(atual);
        if (id == null) nova.delete('frente');
        else nova.set('frente', String(id));
        return nova;
      },
      // Sem entrada nova no histórico: abrir e fechar o modal cinco vezes não pode
      // exigir cinco cliques no "voltar" para sair da página.
      { replace: true }
    );
  }

  /**
   * Abrir a frente marca que você a viu — o mesmo gesto do Dev Board, na mesma rota.
   *
   * <p>Marca ao <b>abrir</b>, não ao fechar: quem abre e lê metade leu, e esperar o
   * fechamento deixaria o aviso aceso em quem trocou de aba com o modal aberto.
   *
   * <p>Não recarrega o roadmap depois — apagar a bolinha debaixo do dedo, enquanto a
   * pessoa lê, repintaria a timeline inteira por uma informação que ela acabou de
   * consumir. O cache local é ajustado à mão.
   *
   * <p>Falha em silêncio: não conseguir anotar uma leitura não merece um alerta por cima
   * da tela — o pior caso é a bolinha continuar acesa, que é o estado de onde ela veio.
   */
  useEffect(() => {
    if (frenteAberta == null) return;
    void api(`/api/tarefas/${frenteAberta}/vista`, { method: 'POST' })
      .then(() =>
        filaDeQueries.setQueryData<RoadmapDados>(CHAVE_ROADMAP, (r) =>
          !r
            ? r
            : {
                ...r,
                tarefas: r.tarefas.map((t) =>
                  t.id === frenteAberta ? { ...t, naoVisto: false } : t
                ),
              }
        )
      )
      .catch(() => {});
  }, [frenteAberta, filaDeQueries]);
  /**
   * Em qual bloco o formulário de nova frente está aberto — nulo quando fechado.
   *
   * <p>Era um booleano, e o formulário morava acima da timeline com um seletor de
   * "Bloco" dentro. O botão que o abria ficava no fim da barra de filtros: como ela é
   * `nowrap` com rolagem horizontal, os nove blocos empurravam o botão para fora da
   * tela em qualquer janela menor que ~1810px. Ele nunca foi removido — só nunca coube.
   *
   * <p>Guardar o bloco em vez de um sim/não é o que permite a frente nascer onde o
   * clique aconteceu, sem perguntar de novo uma coisa que o botão já disse.
   *
   * <p>{@code 'livre'} é o caso do Kanban, cujas colunas são <b>status</b> e não blocos:
   * ali o clique não tem como dizer em que bloco a frente nasce, e o formulário volta a
   * perguntar. Um id ou {@code 'livre'}, e não um booleano ao lado do id, porque os dois
   * estados se excluem — abrir um fecha o outro, e representá-los junto permitiria a
   * combinação impossível de estar aberto em dois lugares.
   */
  const [novaEm, setNovaEm] = useState<number | 'livre' | null>(null);
  const [configAberta, setConfigAberta] = useState(false);
  const [arrastando, setArrastando] = useState<number | null>(null);
  const [alvo, setAlvo] = useState<AlvoDrag | null>(null);
  const campoDeArquivo = useRef<HTMLInputElement>(null);
  const [notaBackup, setNotaBackup] = useState<{ texto: string; ok: boolean } | null>(null);

  /** Baixa o roadmap no formato do protótipo — o arquivo restaura no GG Pilot v1.6. */
  async function exportarBackup() {
    try {
      const conteudo = await api<unknown>(`/api/projetos/${SLUG}/exportar`);
      const blob = new Blob([JSON.stringify(conteudo, null, 2)], { type: 'application/json' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `gg-pilot-backup-${new Date().toISOString().slice(0, 10)}.json`;
      link.click();
      URL.revokeObjectURL(link.href);
      setNotaBackup({ texto: 'Backup baixado.', ok: true });
    } catch (e) {
      setNotaBackup({ texto: e instanceof Error ? e.message : 'Falha ao exportar.', ok: false });
    }
  }

  /** Aceita o backup completo do v1.6, o export do roadmap ou o export de horas. */
  async function importarBackup(arquivo: File) {
    try {
      const resultado = await api<{ tarefasCriadas: number; tarefasAtualizadas: number }>(
        '/api/admin/importar',
        { method: 'POST', body: await arquivo.text() }
      );
      // Invalida tudo: a importação mexe em tarefas, horas e no que mais o arquivo
      // trouxer — e adivinhar quais chaves sobraram intactas seria adivinhar errado.
      await filaDeQueries.invalidateQueries();
      setNotaBackup({
        texto: `Importado: ${resultado.tarefasCriadas} novas, ${resultado.tarefasAtualizadas} atualizadas.`,
        ok: true,
      });
    } catch (e) {
      setNotaBackup({ texto: e instanceof Error ? e.message : 'Falha ao importar.', ok: false });
    }
  }

  const { data, isPending, error } = useQuery<RoadmapDados>({
    queryKey: CHAVE_ROADMAP,
    queryFn: () => api(`/api/projetos/${SLUG}/roadmap`),
  });

  // O "mês atual" deixou de ser escolha e virou leitura do calendário, então a mutation
  // que gravava `projeto.mesAtual` saiu daqui. O endpoint continua de pé no servidor
  // para quem restaura um backup do protótipo, mas nenhuma tela mais o chama.

  const configurarDuracao = useMutation({
    mutationFn: (mesesTotal: number) =>
      api(`/api/projetos/${SLUG}`, {
        method: 'PATCH',
        body: JSON.stringify({ mesesTotal }),
      }),
    onSuccess: () => {
      void filaDeQueries.invalidateQueries({ queryKey: CHAVE_ROADMAP });
      setConfigAberta(false);
    },
  });

  const membrosPorId = useMemo(() => {
    const mapa = new Map<number, Membro>();
    data?.membros.forEach((m) => mapa.set(m.id, m));
    return mapa;
  }, [data]);

  if (isPending) return <div className="rm-estado">carregando o roadmap…</div>;
  if (error) return <div className="rm-estado">não consegui carregar: {error.message}</div>;

  const { projeto, meses, grupos, tarefas, kpis } = data;
  const mesSeason = tarefas.find((t) => t.marcoSeason)?.mesInicio ?? null;

  /**
   * O mês de hoje, achado no calendário — não mais escolhido à mão.
   *
   * <p>"Estamos aqui" era um campo que alguém precisava lembrar de mudar todo dia 1º, e
   * um cronograma que aponta para o mês errado é pior do que um que não aponta nada. A
   * timeline já sabe o {@code anoMes} de cada coluna; basta perguntar ao relógio.
   *
   * <p>Nulo quando hoje cai fora da timeline (antes do início ou depois do fim) — aí não
   * há coluna para marcar, e a tela diz isso em vez de fingir que o projeto está no mês 1.
   */
  const hoje = new Date();
  const anoMesDeHoje = `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}`;
  const mesCorrente = meses.find((m) => m.anoMes === anoMesDeHoje)?.indice ?? null;

  /** Onde no mês estamos, de 0 a 1 — é o que posiciona o risco do dia dentro da coluna. */
  const diasNoMes = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0).getDate();
  const fracaoDoMes = (hoje.getDate() - 0.5) / diasNoMes;
  const dataPorExtenso = hoje.toLocaleDateString('pt-BR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  const visiveis = grupos.filter((g) => !ocultos.has(g.id));
  const selecionada = frenteAberta != null ? tarefas.find((t) => t.id === frenteAberta) : null;

  function alternarGrupo(id: number) {
    setOcultos((atual) => {
      const novo = new Set(atual);
      if (novo.has(id)) novo.delete(id);
      else novo.add(id);
      return novo;
    });
  }

  function doBloco(grupoId: number): Tarefa[] {
    return tarefas.filter((t) => t.grupoId === grupoId);
  }

  /**
   * Quantas linhas a grade tem, contadas do mesmo jeito que o corpo as emite: uma para
   * o cabeçalho, uma por faixa de bloco, mais uma quando o formulário de nova frente
   * está aberto nele, e duas por frente — a da barra e a do separador.
   *
   * É o fim da coluna de destaque do mês, e ele precisa ser contado à mão porque
   * `grid-row: 1 / -1` NÃO funciona aqui. O `-1` conta a partir do fim do grid
   * *explícito*, e `.grade` declara só `grid-template-columns`: as linhas são todas
   * implícitas, criadas pelo `gridRow` de cada célula. Sem nenhuma linha explícita, o
   * `-1` cai na mesma linha que o `1` — e a coluna, que deveria varrer a timeline
   * inteira, ficava com a altura de uma linha só, escondida atrás do cabeçalho.
   *
   * Era esse o sintoma: via-se o brilho dourado ao redor do mês e nada descendo. A
   * coluna rubi da SEASON 1 sofria do mesmo mal, pelo mesmo motivo.
   */
  const totalDeLinhas =
    1 +
    visiveis.reduce(
      (soma, g) => soma + 1 + (novaEm === g.id ? 1 : 0) + doBloco(g.id).length * 2,
      0,
    );

  /** Converte o alvo visual do arrasto no comando {grupoId, aposTarefaId} da API. */
  function soltarEm(alvoFinal: AlvoDrag) {
    const id = arrastando;
    setArrastando(null);
    setAlvo(null);
    if (id == null) return;

    if (alvoFinal.tipo === 'bloco') {
      const ultima = doBloco(alvoFinal.grupoId).filter((t) => t.id !== id).at(-1);
      moverTarefa.mutate({ id, grupoId: alvoFinal.grupoId, aposTarefaId: ultima?.id ?? null });
      return;
    }

    const referencia = tarefas.find((t) => t.id === alvoFinal.tarefaId);
    if (!referencia || referencia.id === id) return;
    const bloco = doBloco(referencia.grupoId).filter((t) => t.id !== id);
    const indice = bloco.findIndex((t) => t.id === referencia.id);
    const apos = alvoFinal.antes ? (bloco[indice - 1]?.id ?? null) : referencia.id;
    moverTarefa.mutate({ id, grupoId: referencia.grupoId, aposTarefaId: apos });
  }

  const cartoes = [
    { n: kpis.total, l: 'Frentes no plano', c: '#C9A25A' },
    { n: kpis.concluidas, l: 'Concluídas', c: '#E6C787' },
    { n: kpis.emAndamento, l: 'Em andamento', c: '#4E9C6B' },
    { n: kpis.aComecar, l: 'Ainda vão começar', c: '#8E8875' },
    ...(kpis.emAtraso > 0 ? [{ n: kpis.emAtraso, l: 'Em atraso', c: '#B04A45' }] : []),
  ];

  return (
    <div>
      <div className="rm-topo">
        {/* O nome e o ícone são os do menu; a duração e a contagem viram a linha de
            apoio, que era o que o antigo "Cronograma de 12 meses" dizia. */}
        <TituloDaPagina>
          Cronograma de {projeto.mesesTotal} meses · {kpis.total} frentes em {grupos.length}{' '}
          blocos de trabalho · {projeto.nome}
        </TituloDaPagina>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* Não se escolhe mais o mês: ele vem do calendário, com o dia junto. */}
          <div className="hoje-badge">
            <div className="pulse" />
            <div className="tb-txt">
              <div className="l1 data">{dataPorExtenso}</div>
              <div className="l2">
                {mesCorrente != null
                  ? `ESTAMOS AQUI · MÊS ${mesCorrente} DE ${projeto.mesesTotal}`
                  : 'HOJE ESTÁ FORA DA TIMELINE'}
              </div>
            </div>
          </div>
          {/*
            Backup e Restaurar moraram na barra lateral até aqui, com o mesmo peso
            visual dos quinze destinos de navegação. Mas eles não levam a lugar nenhum:
            agem sobre o roadmap, e sobre nada mais. O lugar de uma ação é a tela do que
            ela altera — e é por isso que estão neste cabeçalho, e não no rodapé: aqui
            eles ficam junto do "estamos aqui" e do ⚙, que são as outras coisas que
            mexem no cronograma inteiro.
          */}
          <button
            className="rd-acao"
            title="Baixa o roadmap num arquivo que o protótipo v1.6 também sabe restaurar"
            onClick={() => void exportarBackup()}
          >
            ⤓ Backup
          </button>

          {usuario?.papel === 'ADMIN' && (
            <>
              <button
                className="rd-acao"
                title="Aceita o backup do v1.6, o export do roadmap ou o export de horas"
                onClick={() => campoDeArquivo.current?.click()}
              >
                ⤒ Restaurar
              </button>
              <input
                ref={campoDeArquivo}
                type="file"
                accept="application/json"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const arquivo = e.target.files?.[0];
                  if (arquivo) void importarBackup(arquivo);
                  e.target.value = '';
                }}
              />
              <button
                className="cfg-btn"
                title="Configurar a duração da timeline"
                onClick={() => setConfigAberta((v) => !v)}
              >
                ⚙
              </button>
            </>
          )}
        </div>
      </div>

      {notaBackup && (
        <div className={`rd-nota${notaBackup.ok ? ' ok' : ''}`}>{notaBackup.texto}</div>
      )}

      {configAberta && (
        <ConfigurarTimeline
          projeto={projeto}
          erro={configurarDuracao.error?.message ?? null}
          salvando={configurarDuracao.isPending}
          salvar={(meses) => configurarDuracao.mutate(meses)}
          fechar={() => setConfigAberta(false)}
        />
      )}

      <div className="kpis">
        {cartoes.map((k) => (
          <div key={k.l} className="kpi" style={{ '--k': k.c } as React.CSSProperties}>
            <div className="n" style={{ color: k.c }}>
              {k.n}
            </div>
            <div className="l">{k.l}</div>
          </div>
        ))}
      </div>

      <div className="overall">
        <div className="t">Progresso geral</div>
        <div className="track">
          <div className="fill" style={{ width: `${kpis.progressoPct}%` }} />
        </div>
        <div className="pct">{kpis.progressoPct}%</div>
      </div>

      <div className="filtros">
        <div className="lab">Filtrar</div>
        {grupos.map((g) => (
          <button
            key={g.id}
            className={`chip${ocultos.has(g.id) ? '' : ' on'}`}
            style={{ '--c': g.cor } as React.CSSProperties}
            onClick={() => alternarGrupo(g.id)}
          >
            <span className="sw" />
            {g.nome}
          </button>
        ))}
        {/* O "+ Nova frente" saiu daqui. Esta barra rola na horizontal, e o botão vivia
            depois do último bloco — fora da tela em qualquer janela estreita. Agora há
            um "+" no cabeçalho de cada bloco, que é onde a frente vai nascer. */}
      </div>

      <div className="modos">
        <button
          className={`modo${modo === 'timeline' ? ' on' : ''}`}
          onClick={() => {
            setModo('timeline');
            setNovaEm(null); // o formulário do Kanban não sobrevive à troca de visão
          }}
        >
          ▤ Timeline
        </button>
        <button
          className={`modo${modo === 'kanban' ? ' on' : ''}`}
          onClick={() => {
            setModo('kanban');
            setNovaEm(null);
          }}
        >
          ▦ Kanban
        </button>

        {/* O Kanban não tem faixa de bloco onde pendurar um "+", porque as colunas dele
            são status — e criar uma frente "em atraso" seria mentira: o status vem das
            datas. Então aqui o botão é um só, e o bloco vira pergunta no formulário. */}
        {modo === 'kanban' && (
          <>
            <div style={{ flex: 1 }} />
            <button
              className={`modo nova${novaEm === 'livre' ? ' on' : ''}`}
              onClick={() => setNovaEm((atual) => (atual === 'livre' ? null : 'livre'))}
              aria-expanded={novaEm === 'livre'}
            >
              + Nova frente
            </button>
          </>
        )}
      </div>

      {modo === 'kanban' && novaEm === 'livre' && (
        <NovaFrente
          grupos={grupos}
          meses={data.meses}
          mesAtual={mesCorrente ?? 1}
          criar={(corpo) =>
            criarTarefa.mutate(corpo, {
              onSuccess: (criada) => {
                setNovaEm(null);
                setFrenteAberta(criada.id);
              },
            })
          }
          cancelar={() => setNovaEm(null)}
        />
      )}

      {modo === 'kanban' ? (
        <Kanban dados={data} filtro={ocultos} abrirFrente={(t) => setFrenteAberta(t.id)} />
      ) : (
        <>
          <div className="board">
            <div className="grade" style={{ '--n-meses': meses.length } as React.CSSProperties}>
              {meses.map((m) => (
                <div
                  key={`guia-${m.indice}`}
                  className={`guia${m.indice === mesCorrente ? ' hoje' : ''}${m.indice === mesSeason ? ' season' : ''}`}
                  style={{
                    gridColumn: `${m.indice + 1} / ${m.indice + 2}`,
                    gridRow: `1 / ${totalDeLinhas + 1}`,
                  }}
                >
                  {/* O dia de hoje, dentro da coluna do mês: a coluna diz "estamos em
                      agosto", o risco diz "e já é dia 1". */}
                  {m.indice === mesCorrente && (
                    <span
                      className="risco-hoje"
                      style={{ left: `${fracaoDoMes * 100}%` }}
                      title={`Hoje, ${dataPorExtenso}`}
                    />
                  )}
                </div>
              ))}

              <div className="hcel canto" style={{ gridRow: 1, gridColumn: 1 }}>
                FRENTE DE TRABALHO
              </div>
              {meses.map((m) => (
                <div
                  key={`mes-${m.indice}`}
                  className="hcel"
                  style={{ gridRow: 1, gridColumn: m.indice + 1 }}
                  title={m.indice === mesCorrente ? `Hoje, ${dataPorExtenso}` : `${m.rotulo}/${m.ano}`}
                >
                  {m.indice === mesCorrente ? (
                    <span className="pill">
                      {m.rotulo} · DIA {hoje.getDate()}
                    </span>
                  ) : (
                    m.rotulo
                  )}
                  <span className="yr">{m.ano}</span>
                  {m.indice === mesSeason && <span className="spill">SEASON 1</span>}
                </div>
              ))}

              {(() => {
                let linha = 2;
                return visiveis.map((grupo) => {
                  const bloco = doBloco(grupo.id);
                  const concluidas = bloco.filter((t) => t.concluida).length;
                  const linhaFaixa = linha++;

                  return (
                    <Fragment key={grupo.id}>
                      <div
                        className={`gfaixa${bloco.length === 0 ? ' vazia' : ''}${alvo?.tipo === 'bloco' && alvo.grupoId === grupo.id ? ' over' : ''}`}
                        style={{ '--c': grupo.cor, gridRow: linhaFaixa } as React.CSSProperties}
                        onDragOver={(e) => {
                          if (arrastando == null) return;
                          e.preventDefault();
                          setAlvo({ tipo: 'bloco', grupoId: grupo.id });
                        }}
                        onDragLeave={() => setAlvo(null)}
                        onDrop={() => soltarEm({ tipo: 'bloco', grupoId: grupo.id })}
                      >
                        {/* a faixa tem a largura da grade toda; quem gruda no scroll é o texto */}
                        <span className="gtexto">
                          <span className="t">{grupo.nome}</span>
                          {/* Bloco vazio não mostra "0/0", que não responde nada e ainda
                              deixa uma faixa de cor de largura inteira sem nada embaixo.
                              Ele diz que está vazio — e o "+" ao lado é o convite. */}
                          {bloco.length === 0 ? (
                            <span className="cnt vazio">sem frentes</span>
                          ) : (
                            <span className="cnt">
                              {concluidas}/{bloco.length}
                            </span>
                          )}
                          {/* Dentro do `.gtexto`, e não solto na faixa: é ele que tem o
                              `position: sticky`, então o botão acompanha o nome do bloco
                              quando a timeline rola para os meses da direita. Solto, ele
                              sairia da tela junto com o resto da faixa. */}
                          <button
                            type="button"
                            className={`gmais${novaEm === grupo.id ? ' on' : ''}`}
                            title={
                              novaEm === grupo.id
                                ? 'Fechar'
                                : `Nova frente em ${grupo.nome}`
                            }
                            aria-label={`Nova frente em ${grupo.nome}`}
                            aria-expanded={novaEm === grupo.id}
                            onClick={() =>
                              setNovaEm((atual) => (atual === grupo.id ? null : grupo.id))
                            }
                          >
                            +
                          </button>
                        </span>
                      </div>

                      {/* O formulário nasce como uma linha da própria grade, logo abaixo
                          da faixa: quem clicou no "+" do GAME DESIGN não deve ter de
                          procurar onde o formulário abriu. */}
                      {novaEm === grupo.id && (
                        <div className="gnova" style={{ gridRow: linha++ }}>
                          <div className="gnova-int">
                            <NovaFrente
                              // Remontar ao trocar de bloco zera os campos: reaproveitar
                              // o formulário deixaria o nome digitado no bloco anterior.
                              key={grupo.id}
                              grupo={grupo}
                              meses={data.meses}
                              mesAtual={mesCorrente ?? 1}
                              criar={(corpo) =>
                                criarTarefa.mutate(corpo, {
                                  onSuccess: (criada) => {
                                    setNovaEm(null);
                                    setFrenteAberta(criada.id);
                                  },
                                })
                              }
                              cancelar={() => setNovaEm(null)}
                            />
                          </div>
                        </div>
                      )}

                      {bloco.map((tarefa) => {
                        const linhaTarefa = linha++;
                        const linhaSeparador = linha++;
                        const marcaDrag =
                          alvo?.tipo === 'frente' && alvo.tarefaId === tarefa.id
                            ? alvo.antes
                              ? ' over-top'
                              : ' over-bot'
                            : '';
                        return (
                          <Fragment key={tarefa.id}>
                            <div
                              className={`tlabel${tarefa.concluida ? ' done' : ''}${arrastando === tarefa.id ? ' dragging' : ''}${marcaDrag}`}
                              style={{ gridRow: linhaTarefa, gridColumn: 1 }}
                              title={tarefa.nome}
                              draggable
                              onDragStart={() => setArrastando(tarefa.id)}
                              onDragEnd={() => {
                                setArrastando(null);
                                setAlvo(null);
                              }}
                              onDragOver={(e) => {
                                if (arrastando == null || arrastando === tarefa.id) return;
                                e.preventDefault();
                                const r = e.currentTarget.getBoundingClientRect();
                                setAlvo({
                                  tipo: 'frente',
                                  tarefaId: tarefa.id,
                                  antes: e.clientY - r.top < r.height / 2,
                                });
                              }}
                              onDrop={(e) => {
                                e.preventDefault();
                                if (alvo) soltarEm(alvo);
                              }}
                              onClick={() => setFrenteAberta(tarefa.id)}
                            >
                              <span className="grip" title="Arraste para reordenar">
                                ⠿
                              </span>
                              <button
                                className={`cbox${tarefa.concluida ? ' on' : ''}`}
                                title={tarefa.concluida ? 'Desmarcar' : 'Marcar como concluída'}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  editarTarefa.mutate({ id: tarefa.id, concluida: !tarefa.concluida });
                                }}
                              >
                                <svg viewBox="0 0 12 12">
                                  <polyline points="2,6.4 4.6,9 10,3" />
                                </svg>
                              </button>
                              <span className="nm">{tarefa.nome}</span>
                              {tarefa.checklist.total > 0 && (
                                <span
                                  className={`clbadge${tarefa.checklist.feitos === tarefa.checklist.total ? ' full' : ''}`}
                                >
                                  {tarefa.checklist.feitos}/{tarefa.checklist.total}
                                </span>
                              )}
                              <PilhaDeAvatares ids={tarefa.responsaveis} membros={membrosPorId} />
                            </div>

                            <div
                              className="trilha"
                              style={{
                                gridRow: linhaTarefa,
                                gridColumn: `${Math.min(tarefa.mesInicio, meses.length) + 1} / ${Math.min(tarefa.mesFim, meses.length) + 2}`,
                              }}
                            >
                              <div
                                className={`barra${tarefa.concluida ? ' done' : ''}`}
                                style={{ '--c': grupo.cor } as React.CSSProperties}
                                title={dicaDaBarra(tarefa)}
                                onClick={() => setFrenteAberta(tarefa.id)}
                              >
                                <span className="chk">✓</span>
                              </div>
                            </div>
                            {/* O realce da linha inteira, no hover.
                                Vem DEPOIS do rótulo e da trilha de propósito: sem um
                                elemento que envolva a linha (a grade põe as células
                                soltas), o CSS só alcança este daqui por irmão adjacente
                                — `.tlabel:hover + .trilha + .realce`. Fazer o mesmo com
                                estado do React custaria um re-render da grade inteira a
                                cada linha que o mouse cruza. */}
                            <div className="realce" style={{ gridRow: linhaTarefa }} />
                            <div className="linha-fim" style={{ gridRow: linhaSeparador }} />
                          </Fragment>
                        );
                      })}
                    </Fragment>
                  );
                });
              })()}
            </div>
          </div>

          {/* A legenda era a mesma lista dos filtros logo acima — as nove cores com os
              nove nomes, ditas duas vezes na mesma tela. Quem quer saber a cor de um
              bloco já a tem no chip que usa para filtrar. Sobrou o que os filtros não
              explicam: a barra dourada da frente concluída. */}
          <div className="legenda">
            <span className="sw" />
            BARRA DOURADA = FRENTE CONCLUÍDA
          </div>
        </>
      )}

      {selecionada && (
        <ModalFrente
          tarefa={selecionada}
          grupos={grupos}
          membros={data.membros}
          meses={meses}
          fechar={() => setFrenteAberta(null)}
        />
      )}
    </div>
  );
}

/** Configuração da timeline (só Admin): quantos meses o plano cobre. */
function ConfigurarTimeline({
  projeto,
  erro,
  salvando,
  salvar,
  fechar,
}: {
  projeto: RoadmapDados['projeto'];
  erro: string | null;
  salvando: boolean;
  salvar(meses: number): void;
  fechar(): void;
}) {
  const [meses, setMeses] = useState(projeto.mesesTotal);
  const inicio = new Date(projeto.mesInicio + 'T00:00:00');
  const fim = new Date(inicio.getFullYear(), inicio.getMonth() + meses - 1, 1);
  const rotulo = (d: Date) =>
    d.toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' }).replace('.', '');

  return (
    <div className="newf" style={{ alignItems: 'center' }}>
      <div className="ff">
        <label>Duração da timeline (meses)</label>
        <input
          type="number"
          min={1}
          max={60}
          value={meses}
          onChange={(e) => setMeses(Number(e.target.value))}
          style={{ width: 90, textAlign: 'center', fontWeight: 600 }}
        />
      </div>
      <div style={{ fontSize: 12, color: 'var(--meta)', marginTop: 14 }}>
        {rotulo(inicio)} → <b style={{ color: 'var(--gold)' }}>{rotulo(fim)}</b>
      </div>
      <button className="add" disabled={salvando || meses < 1 || meses > 60} onClick={() => salvar(meses)}>
        {salvando ? 'Salvando…' : 'Salvar'}
      </button>
      <button className="chip" onClick={fechar}>
        Cancelar
      </button>
      {erro && (
        <div style={{ flexBasis: '100%', fontSize: 12, color: '#D98C88' }}>{erro}</div>
      )}
    </div>
  );
}

/**
 * O formulário de nova frente, aberto dentro do bloco em que ela vai nascer.
 *
 * <p><b>Não há mais seletor de "Bloco".</b> Quem escolheu foi o "+" clicado, e repetir a
 * pergunta abriria a porta para a resposta divergir do gesto — clicar no + de MECÂNICAS
 * e criar em DIREÇÃO GERAL sem perceber. O bloco aparece como rótulo de leitura, com a
 * cor dele, para dizer onde se está sem oferecer a troca.
 */
function NovaFrente({
  grupo,
  grupos,
  meses,
  mesAtual,
  criar,
  cancelar,
}: {
  /** O bloco já decidido — o "+" da timeline nasce dentro de um. */
  grupo?: Grupo;
  /** Os blocos entre os quais escolher, quando não há um decidido (o Kanban). */
  grupos?: Grupo[];
  meses: RoadmapDados['meses'];
  mesAtual: number;
  criar(corpo: { nome: string; grupoId: number; mesInicio: number; mesFim: number }): void;
  cancelar(): void;
}) {
  const [nome, setNome] = useState('');
  const [inicio, setInicio] = useState(mesAtual);
  const [fim, setFim] = useState(mesAtual);
  const [escolhido, setEscolhido] = useState(grupo?.id ?? grupos?.[0]?.id ?? 0);
  const grupoId = grupo?.id ?? escolhido;

  function enviar() {
    if (!nome.trim()) return;
    criar({
      nome: nome.trim(),
      grupoId,
      mesInicio: Math.min(inicio, fim),
      mesFim: Math.max(inicio, fim),
    });
    setNome('');
  }

  return (
    <div className="newf">
      <div className="ff">
        <label>Nome da frente</label>
        <input
          className="nm"
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          // Esc fecha: o formulário abriu no meio da timeline, e sair dele não deveria
          // exigir mirar um botão.
          onKeyDown={(e) => {
            if (e.key === 'Enter') enviar();
            if (e.key === 'Escape') cancelar();
          }}
          placeholder="Ex.: Trilha sonora"
          autoFocus
        />
      </div>
      {/* Na timeline o bloco é leitura: quem escolheu foi o "+" clicado, e repetir a
          pergunta abriria a porta para a resposta divergir do gesto. No Kanban não há
          bloco na tela para o clique dizer qual é — as colunas são status —, então ali
          ele volta a ser escolha. */}
      <div className="ff">
        <label>Bloco</label>
        {grupo ? (
          <span className="bloco-fixo" style={{ '--c': grupo.cor } as React.CSSProperties}>
            <span className="sw" />
            {grupo.nome}
          </span>
        ) : (
          <select value={escolhido} onChange={(e) => setEscolhido(Number(e.target.value))}>
            {(grupos ?? []).map((g) => (
              <option key={g.id} value={g.id}>
                {g.nome}
              </option>
            ))}
          </select>
        )}
      </div>
      <div className="ff">
        <label>Início</label>
        <select value={inicio} onChange={(e) => setInicio(Number(e.target.value))}>
          {meses.map((m) => (
            <option key={m.indice} value={m.indice}>
              {m.rotulo}/{String(m.ano).slice(2)}
            </option>
          ))}
        </select>
      </div>
      <div className="ff">
        <label>Fim</label>
        <select value={fim} onChange={(e) => setFim(Number(e.target.value))}>
          {meses.map((m) => (
            <option key={m.indice} value={m.indice}>
              {m.rotulo}/{String(m.ano).slice(2)}
            </option>
          ))}
        </select>
      </div>
      <button className="add" onClick={enviar}>
        Adicionar
      </button>
      <button type="button" className="cancelar" onClick={cancelar}>
        Cancelar
      </button>
    </div>
  );
}

function PilhaDeAvatares({ ids, membros }: { ids: number[]; membros: Map<number, Membro> }) {
  const lista = ids.map((id) => membros.get(id)).filter((m): m is Membro => m != null);
  if (lista.length === 0) return null;
  return (
    <span className="avs">
      {lista.slice(0, 4).map((m) => (
        <Avatar key={m.id} className="av" nome={m.nome} cor={m.cor} url={m.avatar} />
      ))}
    </span>
  );
}

function dicaDaBarra(tarefa: Tarefa): string {
  const duracao = tarefa.mesFim - tarefa.mesInicio + 1;
  const situacao =
    tarefa.status === 'CONCLUIDA'
      ? ' · concluída'
      : tarefa.status === 'EM_ANDAMENTO'
        ? ' · em andamento'
        : tarefa.status === 'EM_ATRASO'
          ? ' · em atraso'
          : '';
  return `${tarefa.nome} · ${duracao} ${duracao > 1 ? 'meses' : 'mês'}${situacao}`;
}

